import React, { useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const GeneralForm = () => {
  const navigate = useNavigate();

  // Dropdown options
  const countryOptions = [
    { label: "United States", value: "US" },
    { label: "Canada", value: "CA" },
    { label: "United Kingdom", value: "UK" },
    // ... add more countries
  ];

  const revenueRanges = [
    { label: "Less than $1M", value: "<1M" },
    { label: "$1M - $5M", value: "1-5M" },
    { label: "$5M - $20M", value: "5-20M" },
    { label: "$20M+", value: "20M+" },
  ];

  const currencyOptions = [
    { label: "USD", value: "USD" },
    { label: "EUR", value: "EUR" },
    { label: "GBP", value: "GBP" },
    // ... add more currencies
  ];

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    emailAddress: "",
    companyName: "",
    phoneNumber: "",
    country: null,
    revenue: null,
    revenueCurrency: null,
    employees: "",
    agree: false,
  });

  // Errors state
  const [errors, setErrors] = useState({});

  // Handle field change and clear field error
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Form submit handler with validations
  const handleSubmit = () => {
    const newErrors = {};

    // Validate Full Name: only letters and spaces allowed.
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
    } else if (!/^[A-Za-z\s]+$/.test(formData.fullName.trim())) {
      newErrors.fullName = "Full Name must contain only alphabets and spaces";
    }

    // Validate Email Address with basic pattern.
    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = "Email Address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress.trim())) {
      newErrors.emailAddress = "Email Address is invalid";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company Name is required";
    }

    // Validate Phone Number: only digits allowed.
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
    } else if (!/^\d+$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = "Phone Number must contain only numbers";
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
    }
    if (!formData.revenue) {
      newErrors.revenue = "Revenue range is required";
    }
    if (!formData.revenueCurrency) {
      newErrors.revenueCurrency = "Revenue Currency is required";
    }

    // Validate Number of Employees: only digits allowed.
    if (!formData.employees.trim()) {
      newErrors.employees = "Number of Employees is required";
    } else if (!/^\d+$/.test(formData.employees.trim())) {
      newErrors.employees = "Number of Employees must contain only numbers";
    }

    if (!formData.agree) {
      newErrors.agree = "You must agree to terms and policy";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      // Clear errors and show success popup
      setErrors({});
      Swal.fire({
        icon: "success",
        title: '<h4 style="color:#407775;font-size: 20px;">Success!</h4>',
        text: "Form submitted successfully!",
        iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
      });
      // Optionally reset fields:
      // ...
    }
  };

  const handleCancel = () => {
    navigate("/csrd");
  };

  return (
    <div className="general-form add-repo">
      <div className="row gx-5">
        {/* Full Name */}
        <div className="form-group col-md-6">
          <label className="formlable" htmlFor="fullName">
            Full Name <span className="mark-form">*</span>
          </label>
          <input
            id="fullName"
            className={`form-control ${errors.fullName ? "is-invalid" : ""}`}
            placeholder="Enter full name"
            value={formData.fullName}
            onChange={(e) => {
              const value = e.target.value;
              // Only update if empty or valid: letters and spaces only.
              if (value === "" || /^[A-Za-z\s]+$/.test(value)) {
                handleChange("fullName", value);
              }
            }}
          />
          {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
        </div>

        {/* Email Address */}
        <div className="form-group col-md-6">
          <label className="formlable" htmlFor="emailAddress">
            Email Address <span className="mark-form">*</span>
          </label>
          <input id="emailAddress" className={`form-control ${errors.emailAddress ? "is-invalid" : ""}`} placeholder="Enter email address" value={formData.emailAddress} onChange={(e) => handleChange("emailAddress", e.target.value)} />
          {errors.emailAddress && <div className="invalid-feedback">{errors.emailAddress}</div>}
        </div>

        {/* Company Name */}
        <div className="form-group col-md-6">
          <label className="formlable" htmlFor="companyName">
            Name of the Company <span className="mark-form">*</span>
          </label>
          <input id="companyName" className={`form-control ${errors.companyName ? "is-invalid" : ""}`} placeholder="Enter company name" value={formData.companyName} onChange={(e) => handleChange("companyName", e.target.value)} />
          {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
        </div>

        {/* Phone Number */}
        <div className="form-group col-md-6">
          <label className="formlable" htmlFor="phoneNumber">
            Phone Number <span className="mark-form">*</span>
          </label>
          <input
            id="phoneNumber"
            className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
            placeholder="Enter phone number"
            value={formData.phoneNumber}
            onChange={(e) => {
              const value = e.target.value;
              // Only update if empty or valid: digits only.
              if (value === "" || /^\d+$/.test(value)) {
                handleChange("phoneNumber", value);
              }
            }}
          />
          {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
        </div>

        {/* Country Dropdown */}
        <div className="form-group col-md-6">
          <label className="formlable" htmlFor="country">
            Country <span className="mark-form">*</span>
          </label>
          <Dropdown id="country" className={`form-control wd-100 ${errors.country ? "is-invalid" : ""}`} value={formData.country} options={countryOptions} optionLabel="label" placeholder="Select Country Name" onChange={(e) => handleChange("country", e.value)} />
          {errors.country && <div className="invalid-feedback">{errors.country}</div>}
        </div>

        {/* Revenue & Currency */}
        <div className="col-md-6">
          <div className="row g-3">
            <div className="form-group col-md-6">
              <label className="formlable" htmlFor="revenue">
                Revenue <span className="mark-form">*</span>
              </label>
              <Dropdown id="revenue" className={`form-control wd-100 ${errors.revenue ? "is-invalid" : ""}`} value={formData.revenue} options={revenueRanges} optionLabel="label" placeholder="Select Revenue Range" onChange={(e) => handleChange("revenue", e.value)} />
              {errors.revenue && <div className="invalid-feedback">{errors.revenue}</div>}
            </div>
            <div className="form-group col-md-6">
              <label className="formlable" htmlFor="revenueCurrency">
                Revenue Currency <span className="mark-form">*</span>
              </label>
              <Dropdown id="revenueCurrency" className={`form-control wd-100 ${errors.revenueCurrency ? "is-invalid" : ""}`} value={formData.revenueCurrency} options={currencyOptions} optionLabel="label" placeholder="Select Currency" onChange={(e) => handleChange("revenueCurrency", e.value)} />
              {errors.revenueCurrency && <div className="invalid-feedback">{errors.revenueCurrency}</div>}
            </div>
          </div>
        </div>

        {/* Number of Employees */}
        <div className="form-group col-md-6">
          <label className="formlable" htmlFor="employees">
            No of Employees <span className="mark-form">*</span>
          </label>
          <input
            id="employees"
            className={`form-control ${errors.employees ? "is-invalid" : ""}`}
            placeholder="Enter number of employees"
            value={formData.employees}
            onChange={(e) => {
              const value = e.target.value;
              // Only update if empty or valid: digits only.
              if (value === "" || /^\d+$/.test(value)) {
                handleChange("employees", value);
              }
            }}
          />
          {errors.employees && <div className="invalid-feedback">{errors.employees}</div>}
        </div>
      </div>

      {/* Terms & Policy Checkbox */}
      <div className="row">
        <div className="form-group col-md-12 d-flex align-items-center mb-0">
          <Checkbox inputId="agree" checked={formData.agree} onChange={(e) => handleChange("agree", e.checked)} />
          <label htmlFor="agree" className="formlable mb-0" style={{ marginLeft: "10px" }}>
            I agree to terms and policy
          </label>
        </div>
        {errors.agree && (
          <div className="invalid-feedback" style={{ display: "block", marginLeft: "36px" }}>
            {errors.agree}
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="forms-buttons-container">
        <Button className="next-button" variant="contained" color="primary" onClick={handleSubmit}>
          Submit
        </Button>
        <Button className="outline-btn" variant="contained" color="primary" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default GeneralForm;
